/**
 * PostHog Analytics Configuration
 * Tracks user behavior and events in the CV Builder app
 */

import posthog from 'posthog-js';

// PostHog configuration
const POSTHOG_KEY = 'phc_6JxlElDDLXngnNxpbK4S10Jf7nVu9yi78rUha2FGGEE';
const POSTHOG_HOST = 'https://us.i.posthog.com';

// Initialize PostHog
export const initPostHog = () => {
  if (typeof window !== 'undefined' && !window.posthog?.__loaded) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      session_recording: {
        enabled: true,
      },
      // Respect Do Not Track
      respect_dnt: true,
      // Disable in development (optional)
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') {
          // Uncomment to disable in dev:
          // posthog.opt_out_capturing();
        }
      },
    });
  }
  return posthog;
};

// Event tracking functions
export const trackEvent = (eventName, properties = {}) => {
  posthog.capture(eventName, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
};

// Identify user (when they provide email or create profile)
export const identifyUser = (userId, traits = {}) => {
  posthog.identify(userId, traits);
};

// Reset user (on logout or session clear)
export const resetUser = () => {
  posthog.reset();
};

// ===== Pre-defined Events =====

// CV Creation Events
export const trackCVCreated = (profileId, method = 'chat') => {
  trackEvent('cv_created', {
    profile_id: profileId,
    creation_method: method, // 'chat', 'upload', 'manual'
  });
};

export const trackCVUploaded = (profileId, fileType, success) => {
  trackEvent('cv_uploaded', {
    profile_id: profileId,
    file_type: fileType,
    success: success,
  });
};

export const trackCVUpdated = (profileId, section) => {
  trackEvent('cv_updated', {
    profile_id: profileId,
    section_updated: section,
  });
};

// PDF Events
export const trackPDFDownloaded = (profileId, language) => {
  trackEvent('pdf_downloaded', {
    profile_id: profileId,
    language: language,
  });
};

// Mobile App Export
export const trackAppExport = (profileId) => {
  trackEvent('app_export_initiated', {
    profile_id: profileId,
  });
};

export const trackQRCodeScanned = (profileId) => {
  trackEvent('qr_code_scanned', {
    profile_id: profileId,
  });
};

// Personality Test Events
export const trackPersonalityStarted = (profileId) => {
  trackEvent('personality_test_started', {
    profile_id: profileId,
  });
};

export const trackPersonalityCompleted = (profileId, scores) => {
  trackEvent('personality_test_completed', {
    profile_id: profileId,
    openness: scores?.openness,
    conscientiousness: scores?.conscientiousness,
    extraversion: scores?.extraversion,
    agreeableness: scores?.agreeableness,
    neuroticism: scores?.neuroticism,
  });
};

// Section Events
export const trackSectionAdded = (profileId, sectionType) => {
  trackEvent('section_added', {
    profile_id: profileId,
    section_type: sectionType, // 'references', 'interests', 'volunteering', 'publications', 'custom'
  });
};

// Chat Events
export const trackChatMessageSent = (sessionId, messageType = 'text') => {
  trackEvent('chat_message_sent', {
    session_id: sessionId,
    message_type: messageType, // 'text', 'voice'
  });
};

// Onboarding Events
export const trackOnboardingStep = (step, completed = true) => {
  trackEvent('onboarding_step', {
    step: step,
    completed: completed,
  });
};

// Lead Events
export const trackLeadCaptured = (email, source) => {
  trackEvent('lead_captured', {
    email_domain: email?.split('@')[1] || 'unknown',
    source: source,
  });
};

// Error Events
export const trackError = (errorType, errorMessage, context = {}) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    ...context,
  });
};

// Feature Usage
export const trackFeatureUsed = (featureName, context = {}) => {
  trackEvent('feature_used', {
    feature: featureName,
    ...context,
  });
};

// ===== CV Funnel Events =====

export const trackCVFirstMessage = (sessionId) => {
  trackEvent('cv_first_message', { session_id: sessionId });
};

export const trackCV50Percent = (sessionId) => {
  trackEvent('cv_50_percent', { session_id: sessionId });
};

export const trackCV90Percent = (sessionId) => {
  trackEvent('cv_90_percent', { session_id: sessionId });
};

export const trackCVDownloadInitiated = (sessionId, method) => {
  trackEvent('cv_download_initiated', { session_id: sessionId, method });
};

export const trackCVDownloadCompleted = (sessionId) => {
  trackEvent('cv_download_completed', { session_id: sessionId });
};

// Page timing
export const trackPageTiming = (pageName, loadTime) => {
  trackEvent('page_load_time', {
    page: pageName,
    load_time_ms: loadTime,
  });
};

export default posthog;
